import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, getMyFeedback, listMyFeedback, markFeedbackDelivered, replyMyFeedback, type MyNote, type MyThread } from "../api";
import { t } from "../i18n";

const MAX_REPLY = 5000;

/** Developer replies of the note not shown yet; an older server without the counter is judged by the status. */
const pending = (n: MyNote) => n.undelivered ?? (n.status === "answered" ? 1 : 0);

const day = (d: string) => new Date(`${d.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/**
 * The user's own notes with their threads (ADR-0049, BL-153). The «Your notes» block is collapsed; only the list (with
 * the «Developer replied» count) is loaded up front. Opening the block (`open()`, or the `#feedback-<id>` anchor) is the
 * delivery: for every note with a developer reply not shown yet the thread is loaded (so the reply is on the page) and
 * then `POST …/delivered` marks it as shown on the site; only after that the badges go away.
 */
export function useFeedbackNotes(active: boolean, openId: number | null) {
  const [items, setItems] = useState<MyNote[] | null>(null);
  const [opened, setOpened] = useState(openId !== null);
  const [threads, setThreads] = useState<Record<number, MyThread>>({});
  const [fresh, setFresh] = useState<ReadonlySet<number>>(new Set());
  const marking = useRef(new Set<number>());

  const loadThread = useCallback(async (id: number) => {
    const th = await getMyFeedback(id);
    setThreads((p) => ({ ...p, [id]: th }));
    return th;
  }, []);

  const reload = useCallback(async () => {
    try {
      setItems((await listMyFeedback()).items);
    } catch {
      /* the block works without the list */
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    void listMyFeedback()
      .then((r) => alive && setItems(r.items))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [active]);

  // The `#feedback-<id>` anchor opens the block and its note.
  useEffect(() => {
    if (openId !== null) setOpened(true);
  }, [openId]);
  useEffect(() => {
    if (opened && openId !== null && items?.some((n) => n.id === openId)) void loadThread(openId).catch(() => undefined);
  }, [opened, openId, items === null, loadThread]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!items || !opened) return;
    for (const n of items.filter((x) => pending(x) > 0 && !marking.current.has(x.id))) {
      marking.current.add(n.id);
      void (async () => {
        try {
          const th = await loadThread(n.id);
          setFresh((p) => new Set([...p, ...th.replies.filter((r) => r.author === "developer" && !r.delivered).map((r) => r.id)]));
          await markFeedbackDelivered(n.id);
          setItems((p) => p && p.map((x) => (x.id === n.id ? { ...x, undelivered: 0, status: x.status === "answered" ? "delivered" : x.status } : x)));
        } catch {
          /* the badge stays; the next visit tries again */
        } finally {
          marking.current.delete(n.id);
        }
      })();
    }
  }, [items, opened, loadThread]);

  const open = useCallback(() => setOpened(true), []);
  const unread = items ? items.reduce((sum, n) => sum + pending(n), 0) : 0;
  return { items, threads, fresh, loadThread, reload, opened, open, unread, replied: unread > 0 };
}
export type FeedbackNotesState = ReturnType<typeof useFeedbackNotes>;

export function RepliedBadge({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="badge warn fb-badge" data-testid="feedback-replied-badge">
      {t("feedback.replied")}
    </span>
  );
}

function replyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return t("feedback.errSignIn");
    if (e.status === 429 || e.code === "too_many_feedback") return t("feedback.errTooMany");
  }
  return t("feedback.errOther");
}

function AnswerForm({ id, state }: { id: number; state: FeedbackNotesState }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = text.trim();
  const valid = trimmed.length > 0 && trimmed.length <= MAX_REPLY;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await replyMyFeedback(id, trimmed);
      setText("");
      await Promise.all([state.loadThread(id), state.reload()]);
    } catch (err) {
      setError(replyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="fb-reply-form" onSubmit={submit} noValidate>
      <label className="acct-note" htmlFor={`fb-answer-${id}`}>
        {t("feedback.answerLabel")}
      </label>
      <textarea
        id={`fb-answer-${id}`}
        className="input"
        value={text}
        maxLength={MAX_REPLY}
        placeholder={t("feedback.answerPlaceholder")}
        onChange={(e) => setText(e.target.value)}
        data-testid="feedback-answer-text"
      />
      <span className="fb-count">{t("feedback.counter", { n: text.length, max: MAX_REPLY })}</span>
      {error && (
        <div className="msg error" role="alert" data-testid="feedback-answer-error">
          {error}
        </div>
      )}
      <div>
        <button type="submit" className="btn btn-primary" disabled={!valid || busy} data-testid="feedback-answer-send">
          {busy ? t("feedback.sending") : t("feedback.answerSend")}
        </button>
      </div>
    </form>
  );
}

function Note({ n, state, openId }: { n: MyNote; state: FeedbackNotesState; openId: number | null }) {
  const th = state.threads[n.id];
  const hasFresh = !!th && th.replies.some((r) => state.fresh.has(r.id));
  const auto = n.id === openId || hasFresh;
  const [open, setOpen] = useState(auto);
  useEffect(() => {
    if (auto) setOpen(true);
  }, [auto]);
  const [loadFailed, setLoadFailed] = useState(false);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !th) state.loadThread(n.id).then(() => setLoadFailed(false), () => setLoadFailed(true));
  }

  const hasDeveloper = !!th && th.replies.some((r) => r.author === "developer");
  const bodyId = `fb-note-${n.id}`;
  return (
    <li className={`fb-note${n.id === openId ? " is-target" : ""}`} id={`feedback-note-${n.id}`} data-testid="feedback-note" data-note-id={n.id}>
      <button type="button" className="fb-note-head" aria-expanded={open} aria-controls={bodyId} onClick={toggle} data-testid="feedback-note-toggle">
        <span className="fb-note-meta">
          <time dateTime={n.sent_on}>{day(n.sent_on)}</time> · {t(`feedback.kind.${n.kind}`)}
          {hasFresh && <span className="badge warn fb-badge">{t("feedback.newReply")}</span>}
          {!hasFresh && n.replies > 0 && <span className="feedback-note"> · {t("feedback.replyCount", { n: n.replies })}</span>}
        </span>
        {!open && <span className="fb-note-excerpt">{n.excerpt}</span>}
      </button>
      {open && (
        <div className="fb-note-body" id={bodyId}>
          {!th && !loadFailed && <p className="feedback-note">{t("feedback.loading")}</p>}
          {loadFailed && (
            <div className="msg error" role="alert">
              {t("feedback.loadFailed")}
            </div>
          )}
          {th && (
            <div className="fb-thread" data-testid="feedback-thread">
              <div className="fb-reply user" data-author="user" data-testid="feedback-thread-note">
                <p className="acct-note">
                  {t("feedback.author.you")} · <time dateTime={th.created_at}>{new Date(th.created_at).toLocaleString()}</time>
                </p>
                <p>{th.text}</p>
              </div>
              {th.replies.map((r) => (
                <div key={r.id} className={`fb-reply ${r.author}`} data-author={r.author} data-testid="feedback-reply">
                  <p className="acct-note">
                    {t(`feedback.author.${r.author}`)} · <time dateTime={r.created_at}>{new Date(r.created_at).toLocaleString()}</time>
                    {state.fresh.has(r.id) && <span className="badge warn fb-badge">{t("feedback.newReply")}</span>}
                  </p>
                  <p>{r.text}</p>
                </div>
              ))}
            </div>
          )}
          {hasDeveloper && <AnswerForm id={n.id} state={state} />}
        </div>
      )}
    </li>
  );
}

/**
 * «Your notes»: nothing is rendered until the user has at least one note. A collapsed `<details>`: the summary holds the
 * title and, while developer replies are undelivered, a «Developer replied · n» badge; opening it delivers them.
 */
export function FeedbackNotes({ state, openId }: { state: FeedbackNotesState; openId: number | null }) {
  const [expanded, setExpanded] = useState(openId !== null);
  useEffect(() => {
    if (openId !== null) setExpanded(true);
  }, [openId]);
  if (!state.items || state.items.length === 0) return null;
  return (
    <details
      className="fb-notes"
      data-testid="feedback-notes"
      open={expanded}
      onToggle={(e) => {
        const isOpen = e.currentTarget.open;
        setExpanded(isOpen);
        if (isOpen) state.open();
      }}
    >
      <summary className="fb-notes-head" data-testid="feedback-notes-summary">
        <h3>{t("feedback.notesTitle")}</h3>
        {state.unread > 0 && (
          <span className="badge warn fb-badge" data-testid="feedback-notes-badge">
            {t("feedback.repliedCount", { n: state.unread })}
          </span>
        )}
      </summary>
      <ul className="fb-note-list">
        {state.items.map((n) => (
          <Note key={n.id} n={n} state={state} openId={openId} />
        ))}
      </ul>
    </details>
  );
}
